import React from 'react';

import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import Avatar from '@mui/material/Avatar';
import Skeleton from '@mui/material/Skeleton';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';

import { Heart, ArrowRight } from 'lucide-react';

import { COLORS } from '../../../Constant/COLORS';

import { toCapitalize } from '../../../Helpers';
import { getCategoryIcon, hasRealImage } from '../../../Helpers/categoryIcon';

import DefaultServiceImage from '../../../Assets/Images/DefaultService.svg';

function Card({ service, href }) {
  const CategoryIcon = getCategoryIcon(service.name);
  const hasIcon = hasRealImage(service.icon);

  return (
    <MuiCard
      sx={{
        borderRadius: 4,
        overflow: 'hidden',
        height: '100%',
        transition: 'transform .2s ease, box-shadow .2s ease, border-color .2s ease',
        '&:hover': {
          transform: 'translateY(-6px)',
          borderColor: COLORS.teal,
          boxShadow: '0 18px 38px rgba(14, 27, 31, 0.14)',
        },
        '&:hover .card-arrow': { transform: 'translateX(4px)' },
      }}
    >
      <CardActionArea
        href={href}
        sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
      >
        <Box position='relative'>
          <CardMedia
            component='img'
            image={service.banner_image || DefaultServiceImage}
            alt={service.name}
            sx={{ aspectRatio: '1 / 0.78', objectFit: 'cover', objectPosition: 'center top' }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(to top, rgba(1,42,52,0.42), transparent 48%)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              width: 34,
              height: 34,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.92)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Heart size={17} color={COLORS.teal} strokeWidth={1.8} />
          </Box>
          <Stack
            direction='row'
            alignItems='center'
            gap={0.75}
            sx={{
              position: 'absolute',
              left: 10,
              bottom: 10,
              px: 1.25,
              py: 0.5,
              borderRadius: 999,
              bgcolor: 'rgba(255,255,255,0.92)',
              backdropFilter: 'blur(4px)',
            }}
          >
            <CategoryIcon size={14} color={COLORS.teal} strokeWidth={1.8} />
            <Typography variant='caption' fontWeight={600} color={COLORS.ink}>
              {toCapitalize(service.name)}
            </Typography>
          </Stack>
        </Box>

        <CardContent sx={{ flex: 1, width: '100%', bgcolor: COLORS.surface }}>
          <Stack direction='row' alignItems='center' gap={1.5} mb={1.5}>
            <Avatar
              src={hasIcon ? service.icon : undefined}
              alt='Icon'
              sx={{ width: 46, height: 46, bgcolor: COLORS.tealSoft, color: COLORS.teal }}
            >
              {!hasIcon && <CategoryIcon size={22} strokeWidth={1.7} />}
            </Avatar>
            <Box minWidth={0}>
              <Typography variant='subtitle1' fontWeight={600} lineHeight={1.3} noWrap>
                {service.professional_name || 'Professional'}
              </Typography>
              <Typography variant='caption' color={COLORS.inkSoft} display='block'>
                {toCapitalize(service.name)}
              </Typography>
            </Box>
          </Stack>
          <Typography
            color={COLORS.inkSoft}
            mb={1.5}
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              minHeight: '2.6em',
            }}
          >
            {service?.description}
          </Typography>
          <Stack direction='row' alignItems='center' gap={0.75}>
            <Typography variant='caption' fontWeight={700} color={COLORS.teal}>
              View details
            </Typography>
            <ArrowRight
              className='card-arrow'
              size={15}
              color={COLORS.teal}
              style={{ transition: 'transform .2s ease' }}
            />
          </Stack>
        </CardContent>
      </CardActionArea>
    </MuiCard>
  );
}

function SkeletonCard() {
  return (
    <Box sx={{ borderRadius: 4, overflow: 'hidden', border: `1px solid ${COLORS.hairline}` }}>
      <Skeleton
        variant='rectangular'
        sx={{ display: 'block', aspectRatio: '1 / 0.78', width: '100%', height: 'unset' }}
      />
      <Box p={2}>
        <Stack direction='row' gap={2} alignItems='center' mb={2}>
          <Skeleton variant='circular' width={46} height={46} />
          <Box flexGrow='1'>
            <Skeleton width='100%' />
            <Skeleton width='60%' />
          </Box>
        </Stack>
        <Skeleton />
        <Skeleton />
        <Skeleton width='40%' />
      </Box>
    </Box>
  );
}

Card.propTypes = {
  href: PropTypes.string.isRequired,
  service: PropTypes.shape({
    banner_image: PropTypes.any,
    icon: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    professional_name: PropTypes.string,
  }),
};

export default Card;
export { SkeletonCard };
